import React, { useState, useEffect } from 'react';
import PriceModel from '../../Types/PriceModel';
import ProductPageProductPrice from './ProductPageProductPrice';
import BuyButton from '../BuyButton/BuyButton';
import { translate } from '../../Services/translation';
import { addMeasuredWallpaperToCart } from '../../Services/Cart.service';
import { get } from '../../Services/http';
import {
  receive as receiveCart,
  triggerAddToCartAnimation,
} from '../../Actions/Cart.action';
import { useTypedDispatch } from '../../Hooks/useTypedDispatch';
import LoadingSpinner from '../LoadingSpinner';
import MeasuredWallpaperCuttingsData from '../../Types/MeasuredWallpaperCuttingsData';
import ProductSampleItem from '../../Types/ProductSampleItem';
import BuySampleButton from '../BuyButton/BuySampleButton';
import StockStatus from '../../Types/StockStatus';
import { StockStatusLevel } from '../../constants';
import { CustomWallpaperFile } from '../MeasuredWallpaperCutout/CustomWallpaper/CustomWallpaperDropzone';

interface MeasuredWallpaperBuySectionProps {
  width?: number;
  height?: number;
  wallpaperMaterial?: string;
  priceModel: PriceModel;
  calculatedSizeTooSmall: boolean;
  cuttingsData: MeasuredWallpaperCuttingsData;
  articleNumber: string;
  samples?: ProductSampleItem[];
  stockstatus?: StockStatus;
  customWallpaperFile?: CustomWallpaperFile;
  buyButtonDisabled?: boolean;
  buyButtonHidden?: boolean;
  disableBuyOption?: boolean;
  hasMaterialSelector?: boolean;
}
const MeasuredWallpaperBuySection = ({
  width,
  height,
  wallpaperMaterial,
  priceModel,
  calculatedSizeTooSmall,
  cuttingsData,
  articleNumber,
  samples,
  stockstatus,
  customWallpaperFile,
  buyButtonDisabled = false,
  buyButtonHidden = false,
  disableBuyOption = false,
  hasMaterialSelector = false,
}: MeasuredWallpaperBuySectionProps) => {
  const dispatch = useTypedDispatch();
  const [showLoadingSpinner, setShowLoadingSpinner] = useState(false);
  const [calculatedPrice, setCalculatedPrice] = useState<
    PriceModel | undefined
  >();

  useEffect(() => {
    if (!width || !height) {
      setCalculatedPrice(undefined);
      return;
    }

    const loadingSpinnerTimeout = setTimeout(
      () => setShowLoadingSpinner(true),
      200
    );
    get(
      `/api/productpage/${articleNumber}/price?width=${width}&height=${height}&wallpaperMaterial=${wallpaperMaterial}`
    )
      .then(async (res) => setCalculatedPrice(await res.json()))
      .finally(() => {
        clearTimeout(loadingSpinnerTimeout);
        setShowLoadingSpinner(false);
      });
  }, [width, height, articleNumber, wallpaperMaterial]);

  const unitPrice = priceModel.campaignPrice?.price
    ? priceModel.formattedCampaignPrice
    : priceModel.formattedPrice;

  const onBuyButtonClick = async () => {
    if (typeof width === 'undefined' || typeof height === 'undefined')
      return;

    const cart = await addMeasuredWallpaperToCart({
      articleNumber,
      wallWidth: width,
      wallHeight: height,
      wallpaperMaterial: wallpaperMaterial,
      cuttingsData,
      customWallpaperFile,
    });

    dispatch(receiveCart(cart));
    dispatch(triggerAddToCartAnimation());
  };
  return (
    <>
      {!showLoadingSpinner && !calculatedPrice && !hasMaterialSelector && (
        <div className="mb-2">
          <ProductPageProductPrice priceModel={priceModel} />
        </div>
      )}
      {(calculatedPrice || showLoadingSpinner) && (
        <div className="mb-2 flex items-end justify-between text-xl font-bold leading-none">
          {!showLoadingSpinner && (
            <ProductPageProductPrice
              priceModel={calculatedPrice!}
              showUnit={false}
            />
          )}
          {showLoadingSpinner && <LoadingSpinner />}
          {unitPrice && !hasMaterialSelector && (
            <span className="text-md font-normal text-gray-500">
              ({unitPrice} / {priceModel.price?.unitOfMeasurementTitle})
            </span>
          )}
        </div>
      )}
      {!!unitPrice &&
        stockstatus?.level !== StockStatusLevel.Inactive &&
        !disableBuyOption &&
        !buyButtonHidden && (
          <BuyButton
            className="mb-2 mt-2"
            disabled={
              calculatedSizeTooSmall ||
              !width ||
              !height ||
              showLoadingSpinner ||
              buyButtonDisabled
            }
            onClick={onBuyButtonClick}
          >
            {translate('general.add-to-cart')}
          </BuyButton>
        )}
      {stockstatus?.level !== StockStatusLevel.Inactive &&
        !disableBuyOption &&
        samples?.map((sample) => (
          <BuySampleButton
            {...sample}
            className="col-span-4 mb-2 border border-solid border-gray-700"
            key={`sample-${sample.articleNumber}`}
          />
        ))}
    </>
  );
};

export default MeasuredWallpaperBuySection;
